import { Stack } from "@mui/material";
import {
  BaseDrawerActions,
  BaseDrawerBody,
  BaseDrawerContainer,
  BaseDrawerForm,
  BaseDrawerScrollableBox,
  BaseDrawerScrollContentWrapper,
  BaseDrawerScrollHeightBox,
  BaseDrawerShadowContainer,
  BaseDrawerShadowOverlay
} from "@ui/organisms/BaseDrawerShell/BaseDrawerShell.components";
import BaseTypography from "@ui/atoms/BaseTypography/BaseTypography";
import XButton from "@ui/atoms/XButton/XButton";
import { type HeaderTabConfig } from "@ui/organisms/Header/HeaderTabs";
import { HeaderToggleTabs } from "@ui/organisms/Header/HeaderToggleTabs";
import { FormHTMLAttributes, useState } from "react";

export interface BaseDrawerShellTabs {
  tabIndex: number;
  onChange: (tabIndex: number) => void;
  tabs: HeaderTabConfig[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  loading?: boolean;
  title?: string | React.ReactNode;
  subtitle?: string;
  tabs?: BaseDrawerShellTabs;
  drawerWidth?: number;
  actions?: React.ReactNode | null;
  actionsJustify?: "flex-start" | "flex-end" | "space-between";
  scrollableContent?: boolean;
  onContentScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
  formProps?: FormHTMLAttributes<HTMLFormElement>; // use when drawer wraps a form
  /** When true, renders children directly without BaseDrawerBody, scroll wrapper, or actions footer.*/
  customContentLayout?: boolean;
  /** Forwarded to the drawer paper; `${dataTestId}-scroll` is set on the scroll area. */
  dataTestId?: string;
}

interface DrawerHeaderProps {
  title?: string | React.ReactNode;
  subtitle?: string;
  tabs?: BaseDrawerShellTabs;
  loading: boolean;
  onClose: () => void;
}

const DrawerHeader = ({
  loading,
  onClose,
  subtitle,
  tabs,
  title
}: DrawerHeaderProps) => (
  <Stack
    direction="row"
    sx={{
      alignItems: "flex-start",
      justifyContent: "space-between",
      mb: 2
    }}
  >
    <Stack
      sx={{
        flex: 1,
        minWidth: 0
      }}
    >
      {title && (
        <BaseTypography
          text={title}
          variant="h6"
          weight="semiBold"
          loading={loading}
          sx={{ mb: subtitle || tabs ? 1 : 0 }}
        />
      )}
      {subtitle && (
        <BaseTypography
          text={subtitle}
          variant="body2"
          weight="regular"
          sx={{
            color: (theme) => theme.vars.palette.text.secondary,
            mb: tabs ? 1 : 0
          }}
          loading={loading}
        />
      )}
      {tabs && (
        <HeaderToggleTabs
          loading={loading}
          tabIndex={tabs.tabIndex}
          onChange={tabs.onChange}
          tabs={tabs.tabs}
        />
      )}
    </Stack>

    <XButton
      handleClose={onClose}
      sx={{
        flexShrink: 0,
        mr: -3.8,
        mt: -2,
        position: "static"
      }}
    />
  </Stack>
);

interface ScrollableContentProps {
  showShadow: boolean;
  onScroll: (event: React.UIEvent<HTMLDivElement>) => void;
  dataTestId?: string;
  children: React.ReactNode;
}

const ScrollableContent = ({
  children,
  dataTestId,
  onScroll,
  showShadow
}: ScrollableContentProps) => (
  <BaseDrawerScrollHeightBox>
    <BaseDrawerShadowContainer>
      <BaseDrawerShadowOverlay showShadow={showShadow} />
    </BaseDrawerShadowContainer>

    <BaseDrawerScrollableBox
      onScroll={onScroll}
      data-test-id={dataTestId ? `${dataTestId}-scroll` : undefined}
    >
      <BaseDrawerScrollContentWrapper>
        {children}
      </BaseDrawerScrollContentWrapper>
    </BaseDrawerScrollableBox>
  </BaseDrawerScrollHeightBox>
);

export const BaseDrawerShell = ({
  actions,
  actionsJustify = "flex-end",
  children,
  customContentLayout = false,
  dataTestId,
  drawerWidth = 420,
  formProps,
  loading = false,
  onClose,
  onContentScroll,
  open,
  scrollableContent,
  subtitle,
  tabs,
  title
}: Props) => {
  const [showShadow, setShowShadow] = useState(false);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setShowShadow(e.currentTarget.scrollTop > 0);
    onContentScroll?.(e);
  };

  const body = scrollableContent ? (
    <ScrollableContent
      showShadow={showShadow}
      onScroll={handleScroll}
      dataTestId={dataTestId}
    >
      {children}
    </ScrollableContent>
  ) : (
    children
  );

  const content = customContentLayout ? (
    children
  ) : (
    <BaseDrawerBody>
      {body}

      {actions && (
        <BaseDrawerActions sx={{ justifyContent: actionsJustify }}>
          {actions}
        </BaseDrawerActions>
      )}
    </BaseDrawerBody>
  );

  return (
    <BaseDrawerContainer
      anchor="right"
      drawerWidth={drawerWidth}
      open={open}
      onClose={onClose}
      slotProps={{
        // MUI gives the paper role="dialog" but never names it, so every drawer
        // opened as an unnamed dialog. The visible title is the name when it is
        // text; a ReactNode title has no string to borrow, so fall back to a
        // generic one rather than announcing nothing.
        paper: {
          "aria-label": typeof title === "string" ? title : "Drawer",
          "data-test-id": dataTestId
        } as { "data-test-id"?: string; "aria-label"?: string }
      }}
    >
      <Stack
        sx={{
          height: "100%"
        }}
      >
        <DrawerHeader
          title={title}
          subtitle={subtitle}
          tabs={tabs}
          loading={loading}
          onClose={onClose}
        />

        {formProps ? (
          <BaseDrawerForm {...formProps}>{content}</BaseDrawerForm>
        ) : (
          content
        )}
      </Stack>
    </BaseDrawerContainer>
  );
};
